// import { Caveat } from '#~/common/editor/index.js';
import { Protocol } from '#~/control/components/protocol/index.js';
// Per the AutoConfiguredLabel mount contract (Multi-Add Phase 2): mount
// <AutoConfiguredLabel> on the Connection type Group. One label
// represents the whole auto-filled control bloc — not per-input labels
// inside the _Http/_ViscaIp/_Onvif editors. The host/port inputs
// inside those editors come from the design-system editor package;
// adding per-input labels would require modifying that package which is
// out of scope. Per `stay-in-scope` (ai.knowledge/global).
import { AutoConfiguredLabel } from '../auto-configured-label.jsx';
import { Columns } from '../layout.jsx';
import { Preview } from '../preview.jsx';
import { useProducer, useStore } from '../store/hooks.js';
import { Controller } from './controller.jsx';
import { Http } from './http.jsx';
import { Onvif } from './onvif.jsx';
import type { Props } from './types.js';
import { ViscaIp } from './visca-ip.jsx';
import { ViscaSerial } from './visca-serial.jsx';
import { Group } from '@studio/design-system/form';
import { AnimatePresence, motion, Motions } from '@studio/design-system/motion';
import type { HtmlAttributes } from '@studio/design-system/props';
import { Protocol as M_Protocol } from '@studio/models/control';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ComponentType, ReactElement } from 'react';
import { createElement } from 'react';
import { shallow } from 'zustand/shallow';

const Components: Record<M_Protocol, ComponentType<HtmlAttributes>> = {
  [M_Protocol.VISCA_IP]: ViscaIp,
  [M_Protocol.VISCA_SERIAL]: ViscaSerial,
  [M_Protocol.ONVIF]: Onvif,
  [M_Protocol.HTTP]: Http
};

/** `<Control>` panel. */
export const Control = (props: Props): ReactElement => {
  const producer = useProducer();
  // Read `candidateId` alongside the existing `protocol` so the
  // `<AutoConfiguredLabel>` mount can address the depot slice keyed
  // by `(deviceId, fieldPath)`.
  const { protocol, candidateId } = useStore(
    (µ) => ({
      protocol: µ.candidate.control?.protocol,
      candidateId: µ.candidate.id
    }),
    shallow
  );

  const handleProtocolChange = useLatestFn((nextProtocol: T_Maybe<M_Protocol>) => {
    producer(({ candidate, control }) => {
      const { cache } = control;

      // Cache the current control settings.
      if (protocol) {
        cache[protocol] = candidate.control;
      }

      // Restore the saved control settings (if applicable)
      candidate.control = nextProtocol
        ? { ...cache[nextProtocol], protocol: nextProtocol }
        : undefined;
    });
  });

  return (
    <div {...props}>
      {/* <h2>Camera controls</h2> */}

      {/* <Caveat title='Depending on the make and model of your camera, you may be required to reboot the device for any edits to apply.' /> */}

      {/* <HRule /> */}

      <Columns>
        <Columns.Column>
          <h3>Control settings</h3>

          {/* Single "Auto Configured" label representing the entire
              auto-filled control bloc (Multi-Add Phase 2). Field path
              is `control.protocol` (the multi-add and single-add
              recording paths record the protocol enum value). Per-
              input labels inside the _Http/_ViscaIp/_Onvif editors
              are intentionally NOT added — those editors live in the
              design-system package and are out of scope. */}
          <Group
            annotation={
              <AutoConfiguredLabel
                deviceId={candidateId}
                fieldPath='control.protocol'
                value={protocol}
              />
            }
            label='Connection type'
          >
            <Protocol protocol={toAccessor(protocol, handleProtocolChange)} />
          </Group>

          <AnimatePresence>
            {protocol ? (
              <motion.div key={protocol} {...Motions.Fade}>
                {createElement(Components[protocol])}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </Columns.Column>

        <Columns.Column>
          <h3>Video preview</h3>
          <Preview />
          {!!protocol && <Controller />}
        </Columns.Column>
      </Columns>
    </div>
  );
};
